/**
 * SAMBA configuration API schemas
 */

import { z } from 'zod';
import { InternalRole } from '../identity-contracts';
import { PositiveIntSchema } from './common.schemas';
import {
    AllowedWebsiteSchemaIPG,
    AllowedWebsiteSchemaIPW,
    AllowedWebsiteSchemaBase,
    AllowedWebsiteSchemaUnmanaged,
    type MerchantConfig,
    RpaBuilderWebsiteConfigSchema,
    type RoleAssignmentCustomConfig,
    MerchantConfigSchema,
    MerchantConfigStorageWriteSchema,
    RoleAssignmentCustomConfigObjectSchema,
    RoleAssignmentCustomConfigSchema,
    UserRoleAssignmentConfigPatchSchema,
} from '../backend_config.types';
import type {
    CloverRuntimeEnvironmentAvailabilityResponse,
    EffectiveConfigResponse,
    EffectiveConfigResponseV1,
    NavigateMenuIntegratedWebsite,
    NavigateMenuRpaBuilderWebsite,
    NavigateMenuUnmanagedAllowedWebsite,
    NavigateMenuWebsite,
    NavigateMenuWebsitesResponse,
    PatchActiveRoleAssignmentUserRoleAssignmentConfigRequest,
    RoleAssignmentConfigResponse,
    SambaAutoupdateConfig,
    SambaAutoupdateConfigV1,
    SambaInternalRole,
    UpdateConfigRequest,
    UpdateRoleAssignmentConfigRequest,
} from '../samba-config.types';
import { NavigateMenuTargetKind } from '../samba-config.types';
import { ReleaseIdentityV1Schema } from './native-release-catalog.schemas';
import { projectSambaLatestReleaseResponseV1Identity } from './common.schemas';

/**
 * Config response (single config object)
 */
export const ConfigResponseSchema: z.ZodType<MerchantConfig> = MerchantConfigSchema;

const RoleAssignmentUserConfigResponseInputSchema = z.looseObject({});

export const RoleAssignmentUserConfigResponseSchema: z.ZodType<RoleAssignmentCustomConfig> = z
    .unknown()
    .superRefine((config, context) => {
        const inputResult = RoleAssignmentUserConfigResponseInputSchema.safeParse(config);
        if (
            inputResult.success &&
            'role_assignment_custom_config_version' in inputResult.data
        ) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['role_assignment_custom_config_version'],
                message: 'RoleAssignmentCustomConfig storage wrappers are not valid responses.',
            });
        }
    })
    .pipe(RoleAssignmentCustomConfigObjectSchema)
    .meta({
        id: 'RoleAssignmentUserConfigResponse',
        title: 'Role-assignment user configuration response',
        description: 'Current normalized user configuration for a role assignment.',
    });

export const SambaAutoupdateConfigSchema: z.ZodType<SambaAutoupdateConfig> = z.discriminatedUnion(
    'updatesEnabled',
    [
        z.object({ updatesEnabled: z.literal(false) }).strict(),
        z
            .object({
                updatesEnabled: z.literal(true),
                nativeIdentity: ReleaseIdentityV1Schema,
                forceAutoupdate: z.boolean(),
            })
            .strict(),
    ]
);

export const SambaAutoupdateConfigV1Schema: z.ZodType<SambaAutoupdateConfigV1> = z
    .object({
        sambaInstallerVersion: z.string().min(1),
        forceAutoupdate: z.boolean(),
    })
    .strict();

export function downgradeSambaAutoupdateConfigToV1(
    config: SambaAutoupdateConfig
): SambaAutoupdateConfigV1 {
    if (!config.updatesEnabled) {
        throw new Error('Samba autoupdate V1 cannot represent disabled updates.');
    }
    return SambaAutoupdateConfigV1Schema.parse({
        sambaInstallerVersion: projectSambaLatestReleaseResponseV1Identity(config.nativeIdentity)
            .releaseVersion,
        forceAutoupdate: config.forceAutoupdate,
    });
}

export const SambaInternalRoleSchema: z.ZodType<SambaInternalRole> = z.enum(InternalRole);

export const EffectiveConfigResponseExtensionSchema: z.ZodType<{
    sambaAutoupdate: SambaAutoupdateConfig;
    internalRole?: SambaInternalRole;
}> = z.object({
    sambaAutoupdate: SambaAutoupdateConfigSchema,
    internalRole: SambaInternalRoleSchema.optional(),
});

export const EffectiveConfigResponseSchema: z.ZodType<EffectiveConfigResponse> = z.intersection(
    MerchantConfigSchema,
    EffectiveConfigResponseExtensionSchema
).meta({
    id: 'EffectiveConfigResponse',
    title: 'Effective Samba configuration response',
    description:
        'Merged organization and active-role configuration with Samba update and internal-role data.',
});

export const EffectiveConfigResponseV1Schema: z.ZodType<EffectiveConfigResponseV1> = z.intersection(
    MerchantConfigSchema,
    z.object({
        sambaAutoupdate: SambaAutoupdateConfigV1Schema,
        internalRole: SambaInternalRoleSchema.optional(),
    })
);

export function normalizeEffectiveConfigResponseV1(
    response: EffectiveConfigResponseV1
): EffectiveConfigResponse {
    const parsedResponse = EffectiveConfigResponseV1Schema.parse(response);
    const buildVersionMatch = /^build\.(\d+)$/.exec(
        parsedResponse.sambaAutoupdate.sambaInstallerVersion
    );
    return EffectiveConfigResponseSchema.parse({
        ...parsedResponse,
        sambaAutoupdate: {
            updatesEnabled: true,
            nativeIdentity:
                buildVersionMatch === null
                    ? {
                          identityKind: 'release',
                          releaseVersion: parsedResponse.sambaAutoupdate.sambaInstallerVersion,
                      }
                    : {
                          identityKind: 'build',
                          buildNumber: buildVersionMatch[1],
                      },
            forceAutoupdate: parsedResponse.sambaAutoupdate.forceAutoupdate,
        },
    });
}

export function downgradeEffectiveConfigResponseToV1(
    response: EffectiveConfigResponse
): EffectiveConfigResponseV1 {
    const parsedResponse = EffectiveConfigResponseSchema.parse(response);
    return EffectiveConfigResponseV1Schema.parse({
        ...parsedResponse,
        sambaAutoupdate: downgradeSambaAutoupdateConfigToV1(parsedResponse.sambaAutoupdate),
    });
}

export const SambaEffectiveConfigResponseContract = {
    variants: {
        v1: {
            normalize: normalizeEffectiveConfigResponseV1,
        },
        v2: {
            normalize: (response: EffectiveConfigResponse): EffectiveConfigResponse =>
                EffectiveConfigResponseSchema.parse(response),
        },
    },
    writerVariant: 'v2',
} as const;

export const SambaEffectiveConfigResponseDowngradeContract = {
    v1: downgradeEffectiveConfigResponseToV1,
} as const;

export const NavigateMenuTargetKindSchema: z.ZodType<NavigateMenuTargetKind> = z.enum([
    NavigateMenuTargetKind.INTEGRATED_WEBSITE,
    NavigateMenuTargetKind.UNMANAGED_ALLOWED_WEBSITE,
    NavigateMenuTargetKind.RPA_BUILDER_WEBSITE,
]);

export const NavigateMenuIpgWebsiteSchema: z.ZodType<NavigateMenuIntegratedWebsite> =
    AllowedWebsiteSchemaIPG.extend({
        navigate_target_kind: z.literal(NavigateMenuTargetKind.INTEGRATED_WEBSITE),
    });

export const NavigateMenuIpwWebsiteSchema: z.ZodType<NavigateMenuIntegratedWebsite> =
    AllowedWebsiteSchemaIPW.extend({
        navigate_target_kind: z.literal(NavigateMenuTargetKind.INTEGRATED_WEBSITE),
    });

export const NavigateMenuPortalWebsiteSchema: z.ZodType<NavigateMenuIntegratedWebsite> =
    AllowedWebsiteSchemaBase.extend({
        type: z.literal('portal'),
        navigate_target_kind: z.literal(NavigateMenuTargetKind.INTEGRATED_WEBSITE),
    });

export const NavigateMenuUnmanagedAllowedWebsiteSchema: z.ZodType<NavigateMenuUnmanagedAllowedWebsite> =
    AllowedWebsiteSchemaUnmanaged.extend({
        navigate_target_kind: z.literal(NavigateMenuTargetKind.UNMANAGED_ALLOWED_WEBSITE),
    });

export const NavigateMenuRpaBuilderWebsiteSchema: z.ZodType<NavigateMenuRpaBuilderWebsite> =
    AllowedWebsiteSchemaUnmanaged.extend({
        navigate_target_kind: z.literal(NavigateMenuTargetKind.RPA_BUILDER_WEBSITE),
        organization_uuid: z.string().uuid(),
        organization_allowed_website_uuid: z.string().uuid(),
        allowed_website_template_uuid: z.string().uuid().optional(),
        rbs_config: RpaBuilderWebsiteConfigSchema,
    });

export const NavigateMenuWebsiteSchema: z.ZodType<NavigateMenuWebsite> = z.union([
    NavigateMenuIpgWebsiteSchema,
    NavigateMenuIpwWebsiteSchema,
    NavigateMenuPortalWebsiteSchema,
    NavigateMenuUnmanagedAllowedWebsiteSchema,
    NavigateMenuRpaBuilderWebsiteSchema,
]);

export const NavigateMenuWebsitesResponseSchema: z.ZodType<NavigateMenuWebsitesResponse> = z.object(
    {
        websites: z.array(NavigateMenuWebsiteSchema),
    }
).meta({
    id: 'NavigateMenuWebsitesResponse',
    title: 'Navigate menu websites response',
    description: 'Role-scoped integrated, unmanaged, and RBS website targets for navigation.',
});

/**
 * Update config request
 */
export const UpdateConfigRequestSchema: z.ZodType<UpdateConfigRequest> = z.object({
    config: MerchantConfigStorageWriteSchema,
});

export const UpdateRoleAssignmentConfigRequestSchema: z.ZodType<UpdateRoleAssignmentConfigRequest> =
    z
        .object({
            expected_config: RoleAssignmentCustomConfigSchema,
            config: RoleAssignmentCustomConfigSchema,
        })
        .strict();

export const PatchActiveRoleAssignmentUserRoleAssignmentConfigRequestSchema: z.ZodType<PatchActiveRoleAssignmentUserRoleAssignmentConfigRequest> =
    z
        .object({
            userRoleAssignmentConfig: UserRoleAssignmentConfigPatchSchema,
        })
        .meta({
            id: 'PatchActiveRoleAssignmentUserRoleAssignmentConfigRequest',
            title: 'Active role-assignment user configuration patch',
            description: 'User preference fields to merge into the active role assignment.',
        });

/**
 * Update config response
 */
export const UpdateConfigResponseSchema: z.ZodType<MerchantConfig> = MerchantConfigSchema;

/**
 * Combined config response
 */
export const CombinedConfigResponseSchema = z.object({
    user_config: RoleAssignmentUserConfigResponseSchema,
    org_config: ConfigResponseSchema,
});

/**
 * Role assignment config response
 */
export const RoleAssignmentConfigResponseSchema: z.ZodType<RoleAssignmentConfigResponse> = z.object(
    {
        role_assignment_id: z.number(),
        user_config: RoleAssignmentUserConfigResponseSchema,
    }
);

/**
 * Get effective config response
 */
export const GetEffectiveConfigResponseSchema = z.object({
    config: MerchantConfigSchema,
    version: z.string(),
});

export const CloverRuntimeEnvironmentAvailabilityResponseSchema: z.ZodType<CloverRuntimeEnvironmentAvailabilityResponse> =
    z
        .object({
            environments: z
                .object({
                    SANDBOX: z.boolean(),
                    PRODUCTION: z.boolean(),
                })
                .strict(),
        })
        .strict();

/**
 * Common param schemas
 */
export const AuditIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const SessionUuidParamSchema: z.ZodType<string> = z.string().uuid();
export const RoleAssignmentUuidParamSchema: z.ZodType<string> = z.string().uuid();
